import React, { useState, useEffect } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { CalendarRange } from 'lucide-react';
import ChatInterface from '../ChatInterface';
import { GlassButton } from '../ui/GlassButton';
import ContentPlanWizard from '../SEO/ContentPlanWizard';
import { usePermissions } from '../../hooks/usePermissions';
import './ContentLayout.css';

interface ContentContextType {
    selectedProjectId: string | undefined;
    onProjectSelect: (id: string | undefined) => void;
}

const ContentLayout: React.FC = () => {
    const { selectedProjectId, onProjectSelect } = useOutletContext<ContentContextType>();
    const { canAccessModule } = usePermissions();
    const [wizardOpen, setWizardOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    // Seeds pre-filled from a cannibalization "Send to planner" handoff (sessionStorage one-shot)
    const [plannerPrefillSeeds, setPlannerPrefillSeeds] = useState<string[] | undefined>(undefined);

    const hasSeo = canAccessModule('seo');

    // Auto-open content plan wizard when ?openMasterplan=true (from dashboard CTA or cannibalization panel)
    useEffect(() => {
        if (searchParams.get('openMasterplan') === 'true' && selectedProjectId && hasSeo) {
            // Read and consume a one-shot cannibalization seed prefill if present
            const raw = sessionStorage.getItem('aime_planner_prefill');
            if (raw) {
                try {
                    const parsed = JSON.parse(raw);
                    if (parsed.projectId === selectedProjectId && Array.isArray(parsed.seeds) && parsed.seeds.length > 0) {
                        setPlannerPrefillSeeds(parsed.seeds);
                    }
                } catch { /* ignore malformed entry */ }
                // Always remove regardless — avoid stale seeds on a future open
                sessionStorage.removeItem('aime_planner_prefill');
            }
            setWizardOpen(true);
            // Clear the query param so refresh doesn't re-open
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, selectedProjectId, hasSeo]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleWizardClose = () => {
        setWizardOpen(false);
        // Clear prefill seeds when the wizard is closed so a manual re-open
        // (via the "Generate 30-day plan" button) starts fresh with masterplan seeds.
        setPlannerPrefillSeeds(undefined);
    };

    return (
        <div className="content-layout">
            <div className="chat-area">
                {hasSeo && selectedProjectId && (
                    <div className="content-layout-header-actions">
                        <GlassButton
                            variant="secondary"
                            size="sm"
                            onClick={() => setWizardOpen(true)}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                        >
                            <CalendarRange size={14} />
                            Generate 30-day plan
                        </GlassButton>
                    </div>
                )}
                <ChatInterface
                    selectedProjectId={selectedProjectId}
                    onProjectSelect={onProjectSelect}
                />
            </div>

            {hasSeo && selectedProjectId && (
                <ContentPlanWizard
                    isOpen={wizardOpen}
                    onClose={handleWizardClose}
                    projectId={selectedProjectId}
                    initialSeeds={plannerPrefillSeeds}
                />
            )}
        </div>
    );
};

export default ContentLayout;
